/**
 * ActivityModal - Full screen activity list for mobile
 *
 * Features:
 * - Paginated activity list with infinite scroll
 * - Filter by activity type
 * - Pull to refresh
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import apiService, { Activity, ActivityType } from '../../services/api';

interface ActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ACTIVITY_TYPE_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'deposit,withdrawal', label: 'Transactions' },
  { value: 'bot_started,bot_stopped', label: 'Bot' },
  { value: 'position_opened,position_closed,rebalance', label: 'Positions' },
  { value: 'hedge_opened,hedge_adjusted', label: 'Hedging' },
];

const ActivityModal: React.FC<ActivityModalProps> = ({ isOpen, onClose }) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState<string | null>(null);

  const LIMIT = 20;

  const fetchActivities = useCallback(async (reset = false) => {
    const currentOffset = reset ? 0 : offset;

    if (reset) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const response = await apiService.activities.getList({
        type: filter || undefined,
        limit: LIMIT,
        offset: currentOffset,
      });

      if (reset) {
        setActivities(response.activities);
      } else {
        setActivities(prev => [...prev, ...response.activities]);
      }

      setHasMore(response.has_more);
      setOffset(currentOffset + LIMIT);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch activities:', err);
      setError('Failed to load activities');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [offset, filter]);

  // Initial fetch and filter change
  useEffect(() => {
    if (isOpen) {
      setOffset(0);
      fetchActivities(true);
    }
  }, [isOpen, filter]);

  const handleLoadMore = () => {
    if (!isLoadingMore && hasMore) {
      fetchActivities(false);
    }
  };

  const getIconEmoji = (icon: string): string => {
    const iconMap: Record<string, string> = {
      'money': '\uD83D\uDCB0',
      'cash-out': '\uD83D\uDCB8',
      'play': '\u25B6\uFE0F',
      'stop': '\u23F9\uFE0F',
      'chart-up': '\uD83D\uDCC8',
      'chart-down': '\uD83D\uDCC9',
      'refresh': '\uD83D\uDD04',
      'harvest': '\uD83C\uDF3E',
      'shield': '\uD83D\uDEE1\uFE0F',
      'balance': '\u2696\uFE0F',
      'temperature': '\uD83C\uDF21\uFE0F',
      'activity': '\u26A1',
    };
    return iconMap[icon] || '\u2728';
  };

  const getColorClasses = (color: string) => {
    const colors: Record<string, string> = {
      yellow: 'from-yellow-500/20 to-yellow-500/5 border-yellow-500/30',
      orange: 'from-orange-500/20 to-orange-500/5 border-orange-500/30',
      blue: 'from-blue-500/20 to-blue-500/5 border-blue-500/30',
      purple: 'from-purple-500/20 to-purple-500/5 border-purple-500/30',
      green: 'from-green-500/20 to-green-500/5 border-green-500/30',
      red: 'from-red-500/20 to-red-500/5 border-red-500/30',
      cyan: 'from-cyan-500/20 to-cyan-500/5 border-cyan-500/30',
      gray: 'from-gray-500/20 to-gray-500/5 border-gray-500/30',
    };
    return colors[color] || colors.gray;
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (hours < 24) {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      });
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black z-50 flex flex-col"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-800">
          <h2 className="text-xl font-semibold text-white">Activity</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-800 transition-colors"
          >
            <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Filters */}
        <div className="px-4 py-3 overflow-x-auto">
          <div className="flex gap-2">
            {ACTIVITY_TYPE_FILTERS.map(f => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                  filter === f.value
                    ? 'bg-white text-black'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Activity List */}
        <div className="flex-1 overflow-y-auto px-4 py-2">
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="bg-gray-800/50 rounded-lg p-4 animate-pulse">
                  <div className="h-4 bg-gray-700 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-700 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <span className="text-4xl mb-4 block">&#9888;</span>
              <p className="text-gray-400">{error}</p>
              <button
                onClick={() => fetchActivities(true)}
                className="mt-4 px-4 py-2 bg-gray-800 rounded-lg text-sm text-blue-400 hover:bg-gray-700"
              >
                Retry
              </button>
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-12">
              <span className="text-4xl mb-4 block">&#128202;</span>
              <p className="text-gray-400">No activity found</p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {activities.map(activity => (
                  <motion.div
                    key={activity.id}
                    className={`
                      bg-gradient-to-r ${getColorClasses(activity.color)}
                      border rounded-lg p-4 flex items-start gap-3
                    `}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <span className="text-2xl flex-shrink-0">{getIconEmoji(activity.icon)}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white">{activity.title}</p>
                      <p className="text-sm text-gray-400">{activity.description}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDateTime(activity.created_at)}
                      </p>
                    </div>
                    {activity.tx_signature && (
                      <a
                        href={`https://solscan.io/tx/${activity.tx_signature}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-400 hover:text-blue-300"
                      >
                        View
                      </a>
                    )}
                  </motion.div>
                ))}
              </div>

              {/* Load More */}
              {hasMore && (
                <button
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  className="w-full mt-4 py-3 bg-gray-800 rounded-lg text-gray-400 hover:bg-gray-700 disabled:opacity-50"
                >
                  {isLoadingMore ? 'Loading...' : 'Load More'}
                </button>
              )}
            </>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ActivityModal;
